import type { SessionDetail } from '~/gql/sessions/sessions'
import { CertificateCondition, SessionProgressionMode } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import { CERTIFICATE_CONDITION_KEYS } from '~/pages/backoffice/admin/sessions/certificate-rule-form'

type IProps = {
  session: Pick<SessionDetail, 'createdAt' | 'progressionMode' | 'certificateCondition' | 'certificateMinScore' | 'certificateTemplateId'>
  timeZone?: string | null
  /** Nom du modèle d'attestation choisi, quand le lecteur peut lire les modèles. */
  templateName?: string
}

/** Configuration de la session : création, progression, règle d'attestation (#243). */
function SessionInfoCard({ session, timeZone, templateName }: IProps) {
  const { t, i18n } = useTranslation()
  const sequential = session.progressionMode === SessionProgressionMode.SEQUENTIAL
  const minScore = session.certificateCondition === CertificateCondition.SCORE && session.certificateMinScore != null
    ? t('sessionDetail.info.minScore', { score: Math.round(session.certificateMinScore * 100) })
    : null
  const template = !session.certificateTemplateId
    ? t('sessionDetail.info.defaultTemplate')
    : templateName ?? t('sessionDetail.info.customTemplate')

  const rows = [
    { key: 'createdAt', label: t('sessionDetail.info.createdAt'), value: formatSessionDate(session.createdAt, i18n.language, timeZone, DATE_WITH_YEAR) },
    {
      key: 'progression',
      label: t('sessions.progression.label'),
      value: t(sequential ? 'sessions.progression.sequential' : 'sessions.progression.free'),
      hint: t(sequential ? 'sessions.progression.sequential.hint' : 'sessions.progression.free.hint'),
    },
    {
      key: 'certificate',
      label: t('sessions.certificate.label'),
      value: [t(CERTIFICATE_CONDITION_KEYS[session.certificateCondition]), minScore].filter(Boolean).join(' — '),
    },
    { key: 'template', label: t('sessionDetail.info.template'), value: template },
  ]

  return (
    <Card {...tourAnchor('sessionDetail.info')}>
      <CardHeader>
        <CardTitle className="text-lg">{t('sessionDetail.info.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">
          {rows.map(row => (
            <div key={row.key} className="flex flex-col gap-0.5">
              <dt className="text-sm text-muted-foreground">{row.label}</dt>
              <dd className="font-medium">{row.value}</dd>
              {row.hint && <dd className="text-xs text-muted-foreground">{row.hint}</dd>}
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}

export default SessionInfoCard
